"use client";

import type { ReactNode } from "react";
import { buttonClass } from "./buttons";
import { DataUnavailable } from "./DataUnavailable";

/**
 * PractitionerCalendarScreen's read-only half (docs/ux/screens/
 * PractitionerCalendarScreen.md), shared by both frontends (R22 T14).
 *
 * **There is nothing per-person left to *set* here.** H40 removed
 * per-practitioner recurring hours, so a seat's calendar is the clinic's
 * opening hours minus two exceptions: when this person is away (a Blackout),
 * and how many fit in an hour (H41). Those two are the editors below this
 * component; this part just shows the next fortnight and how full it is.
 *
 * **An hour another clinic holds says only "Unavailable"** (H38). Where that
 * person is that morning is the other clinic's business, not this one's, and
 * the count is deliberately withheld rather than shown as full.
 */
export type SeatCalendarLink = (props: {
  href: string;
  className?: string;
  children: ReactNode;
}) => ReactNode;

/** One hour of the fortnight, already reduced to what the screen renders. */
export interface SeatBlockView {
  startMinutes: number;
  timeLabel: string;
  /** "2 of 3", or "Unavailable" for an hour another clinic holds (H38). */
  label: string;
  /** Full, taken elsewhere, or otherwise not bookable: rendered as muted. */
  full: boolean;
}

export interface SeatDayView {
  date: string;
  dayLabel: string;
  blocks: SeatBlockView[];
}

export function SeatCalendar({
  practitionerName,
  hasClinicHours,
  days,
  Link,
}: {
  practitionerName: string;
  /** False when the clinic has set no hours, so nobody has a calendar at all. */
  hasClinicHours: boolean;
  days: SeatDayView[];
  Link: SeatCalendarLink;
}) {
  return (
    <>
      <h1 className="mb-6 text-xl font-bold text-ink">{practitionerName}</h1>

      <section className="rounded-xl border border-line bg-surface p-5 shadow-sm">
        <h2 className="mb-1 font-bold text-ink">The next two weeks</h2>
        {!hasClinicHours ? (
          <>
            <p className="mb-3 text-sm text-body">
              Your clinic has no opening hours yet, so nobody has a calendar.
            </p>
            <Link
              href="/business/hours"
              className="text-sm font-semibold text-accent hover:underline"
            >
              Set your opening hours
            </Link>
          </>
        ) : days.length === 0 ? (
          <p className="text-sm text-body">Nothing open in the next two weeks.</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {days.map((day) => (
              <li key={day.date}>
                <p className="text-sm font-semibold text-ink">{day.dayLabel}</p>
                <ul className="mt-1 flex flex-wrap gap-1.5">
                  {day.blocks.map((block) => (
                    <li key={block.startMinutes}>
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold ${
                          block.full
                            ? "border-line bg-page text-muted"
                            : "border-accent/40 bg-accent/10 text-accent"
                        }`}
                      >
                        {block.timeLabel}
                        <span className="font-mono">{block.label}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

/** The tools gate (J2#2), the same card ScheduleScreen shows. */
export function ScheduleToolsLocked({ Link }: { Link: SeatCalendarLink }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-6 shadow-sm">
      <h2 className="mb-1 font-bold text-ink">
        Add your first service to unlock your schedule.
      </h2>
      <p className="mb-4 text-sm text-body">
        Your business tools unlock once at least one service exists.
      </p>
      <Link
        href="/business/services"
        className={buttonClass("primary", "inline-block")}
      >
        Add your first service
      </Link>
    </div>
  );
}

/** The load failure, so both hosts word it the same way. */
export function SeatCalendarUnavailable({
  configured,
}: {
  configured: boolean;
}) {
  return (
    <DataUnavailable
      configured={configured}
      what="this practitioner's calendar"
    />
  );
}
